/**
 * Heatmap: weekday x hour arrivals on the sequential blue ramp. Each series is a row (e.g. "Mon"), its points are the
 * columns (x = hour key; `chart.categories` fixes the column order). A real 0 is the quiet muted fill; a null cell has
 * no fill and reads "no data". One tab stop; arrows move between cells, Enter drills. Owner: slice web-charts (#1115).
 */
import * as React from 'react'
import { HEIGHT, INK, MARK, SEQ_SLOTS, seqColor } from './chart-theme'
import { ChartTooltip, tooltipText } from './chart-tooltip'
import { formatValue, xLabel } from './format'
import { labelEvery, useWidth } from './geometry'
import { buildModel, type ChartProps } from './model'

export function Heatmap({ chart, onPointClick, canSee }: ChartProps) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const model = React.useMemo(() => buildModel(chart, canSee), [chart, canSee])
  const [hover, setHover] = React.useState<{ i: number; s: number } | null>(null)
  const { xs, plotted } = model
  const rowH = HEIGHT.heatmapRow
  const head = 18
  const labelW = 44
  const height = head + plotted.length * rowH
  const pw = Math.max(1, width - labelW - 4)
  const cellW = xs.length > 0 ? pw / xs.length : pw
  const cellH = rowH - MARK.gap
  const max = model.max > 0 ? model.max : 1
  const every = labelEvery(xs.length, pw, 28)

  const cellFill = (v: number | null) => (v === null ? 'none' : v <= 0 ? 'var(--color-muted)' : seqColor(v / max))
  const rowsAt = (i: number, s: number) => {
    const p = plotted[s]
    return p ? [{ key: p.key, label: p.label, value: p.values[i] ?? null, n: p.ns[i] ?? null }] : []
  }
  const titleAt = (i: number, s: number) => `${plotted[s]?.label ?? ''} ${xLabel(chart, xs[i] ?? '')}`.trim()

  const drill = (h: { i: number; s: number } | null) => {
    if (!h || !onPointClick) return
    const p = plotted[h.s]
    const x = xs[h.i]
    if (!p || x === undefined || (p.values[h.i] ?? null) === null) return
    onPointClick({ x, series: p.key })
  }

  /** The cell under a pointer position (client coordinates); null when the plot has no layout box yet. */
  const cellAt = (el: HTMLElement, clientX: number, clientY: number): { i: number; s: number } | null => {
    const box = el.getBoundingClientRect()
    if (box.width <= 0 || box.height <= 0) return null
    const i = Math.max(0, Math.min(xs.length - 1, Math.floor(((clientX - box.left) / box.width) * xs.length)))
    const s = Math.max(0, Math.min(plotted.length - 1, Math.floor(((clientY - box.top) / box.height) * plotted.length)))
    return { i, s }
  }
  const fromPointer = (e: React.PointerEvent<HTMLButtonElement>) => {
    const h = cellAt(e.currentTarget, e.clientX, e.clientY)
    if (h) setHover(h)
  }

  // A pointer click (mouse or touch tap: detail > 0) drills the cell under the pointer, never the focus default; a
  // keyboard Enter/Space click (detail 0) drills the cell the arrows moved to.
  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0) {
      const h = cellAt(e.currentTarget, e.clientX, e.clientY)
      if (h) setHover(h)
      drill(h)
      return
    }
    drill(hover)
  }

  const onKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (xs.length === 0 || plotted.length === 0) return
    const h = hover ?? { i: 0, s: 0 }
    let next = h
    if (e.key === 'ArrowRight') next = { ...h, i: Math.min(xs.length - 1, h.i + 1) }
    else if (e.key === 'ArrowLeft') next = { ...h, i: Math.max(0, h.i - 1) }
    else if (e.key === 'ArrowDown') next = { ...h, s: Math.min(plotted.length - 1, h.s + 1) }
    else if (e.key === 'ArrowUp') next = { ...h, s: Math.max(0, h.s - 1) }
    else return
    e.preventDefault()
    setHover(next)
  }

  const summary = `${chart.title}. Heatmap of ${plotted.length} rows by ${xs.length} columns, darker is more, highest ${formatValue(chart.unit, model.hasData ? model.max : null)}. The table view lists every value.`

  return (
    <div ref={ref} className="reports-root relative w-full" data-chart-kind="heatmap">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label={summary}
        className="block overflow-visible"
      >
        <g>
          {xs.map((x, i) =>
            i % every === 0 ? (
              <text key={x} x={labelW + cellW * i + cellW / 2} y={12} textAnchor="middle" fontSize={10} fill={INK.secondary}>
                {xLabel(chart, x)}
              </text>
            ) : null,
          )}
          {plotted.map((p, s) => (
            <text
              key={p.key}
              x={labelW - 6}
              y={head + s * rowH + cellH / 2}
              dy="0.32em"
              textAnchor="end"
              fontSize={11}
              fill={INK.secondary}
            >
              {p.label}
            </text>
          ))}
        </g>
        <g>
          {plotted.map((p, s) =>
            xs.map((x, i) => {
              const v = p.values[i] ?? null
              const isHover = hover?.i === i && hover.s === s
              return (
                <rect
                  key={`${p.key}:${x}`}
                  data-cell
                  data-x={x}
                  data-series={p.key}
                  data-empty={v === null ? 'true' : undefined}
                  x={labelW + cellW * i + MARK.gap / 2}
                  y={head + s * rowH}
                  width={Math.max(1, cellW - MARK.gap)}
                  height={cellH}
                  rx={2}
                  fill={cellFill(v)}
                  stroke={isHover ? INK.primary : v === null ? INK.grid : 'none'}
                  strokeWidth={isHover ? 1.5 : 1}
                  strokeDasharray={v === null && !isHover ? '2 2' : undefined}
                />
              )
            }),
          )}
        </g>
      </svg>
      <button
        type="button"
        data-plot
        aria-label={
          hover === null
            ? `${chart.title}: use the arrow keys to read cells, Enter to open the cases`
            : tooltipText(titleAt(hover.i, hover.s), rowsAt(hover.i, hover.s), chart.unit)
        }
        aria-disabled={onPointClick ? undefined : true}
        disabled={xs.length === 0 || plotted.length === 0}
        className="absolute block rounded-sm bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring"
        style={{
          left: labelW,
          top: head,
          width: pw,
          height: Math.max(1, plotted.length * rowH),
          cursor: onPointClick ? 'pointer' : 'default',
        }}
        onKeyDown={onKey}
        onFocus={() => setHover((h) => h ?? { i: 0, s: 0 })}
        onBlur={() => setHover(null)}
        onPointerDown={fromPointer}
        onPointerMove={fromPointer}
        onPointerLeave={() => setHover(null)}
        onClick={onClick}
      />
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground" aria-hidden="true">
        <span>Fewer</span>
        <span className="flex">
          {SEQ_SLOTS.map((slot) => (
            <span key={slot} className="h-2.5 w-5" style={{ background: `var(${slot})` }} />
          ))}
        </span>
        <span>More</span>
      </div>
      {hover !== null ? (
        <ChartTooltip
          x={labelW + cellW * hover.i + cellW / 2}
          y={head + hover.s * rowH + rowH}
          containerWidth={width}
          title={titleAt(hover.i, hover.s)}
          rows={rowsAt(hover.i, hover.s)}
          unit={chart.unit}
        />
      ) : null}
    </div>
  )
}
